import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  WorkdayIntegrationConfig,
  WorkdayWriteField
} from '@dadohr/shared/domainTypes/integrations/workday';
import { FS } from '@dadohr/shared/versions';
import shortid from 'shortid';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../services/firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../../services/firecache/documentListener';
import {
  callFirebaseFunction,
  useFirebaseFunction
} from '../../../../../../services/firebaseFunctions';
import { now } from '../../../../../../services/time';

export const toWorkdayConfigDoc = generateToDocFn<WorkdayIntegrationConfig>(
  (d) => {
    d.endpoints = d.endpoints || [];
    d.writeFields = d.writeFields || [];
    return d;
  }
);

/**
 * Seed catalog of Workday write fields (SuccessAcademies new-hire fields, per
 * the ticket). Used to pre-fill a new org's writeFields in the editor and to
 * seed existing orgs — NOT read directly by the picker (that reads the org's
 * own `config.writeFields`). Keys are `"<customObjectAlias>.<fieldAlias>"` and
 * are PLACEHOLDERS until the real Web Service Aliases are confirmed.
 */
export const WORKDAY_DEFAULT_WRITE_FIELDS: WorkdayWriteField[] = [
  { key: 'dadoInbound.startDate', label: 'Start Date', type: 'date' },
  { key: 'dadoInbound.salary', label: 'Salary', type: 'amount' },
  { key: 'dadoInbound.hourly', label: 'Hourly', type: 'amount' },
  { key: 'dadoInbound.includeSignOnBonus', label: 'Include Sign on Bonus', type: 'boolean' },
  { key: 'dadoInbound.signOnAmount', label: 'Sign-on Amount', type: 'amount' },
  { key: 'dadoInbound.relocationBonus', label: 'Relocation Bonus', type: 'boolean' },
  { key: 'dadoInbound.relocationAmount', label: 'Relocation Amount', type: 'amount' },
  { key: 'dadoInbound.teachingBusinessTitle', label: 'Teaching Business Title', type: 'text' },
  { key: 'dadoInbound.businessTitle', label: 'Business Title', type: 'text' },
  { key: 'dadoInbound.borough', label: 'Borough', type: 'reference' },
  { key: 'dadoInbound.workLocation', label: 'Work Location', type: 'reference' },
  { key: 'dadoInbound.jobProfile', label: 'Job Profile', type: 'reference' },
  { key: 'dadoInbound.hireType', label: 'Hire Type', type: 'text' },
  { key: 'dadoInbound.workerType', label: 'Worker Type', type: 'text' },
  { key: 'dadoInbound.dayOneLocation', label: 'Day One Location', type: 'reference' },
  { key: 'dadoInbound.trainingType', label: 'Training Type', type: 'text' },
  { key: 'dadoInbound.academicYear', label: 'Academic Year', type: 'text' },
  { key: 'dadoInbound.costCenter', label: 'Cost Center', type: 'reference' },
  { key: 'dadoInbound.hiringManager', label: 'Hiring Manager', type: 'reference' },
  { key: 'dadoInbound.futurePlacement', label: 'Future Placement', type: 'reference' }
];

const COLLECTION = FS.workdayIntegrationConfig;
const collection = () => store().collection(COLLECTION);
export const workdayConfigStore = createDocumentListenerGetter(
  (orgId) => collection().doc(orgId),
  toWorkdayConfigDoc
);

export const useWorkdayConfig = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useDocumentListener(workdayConfigStore(orgId)),
    (d) => {
      if (!d) {
        const n = now();
        const newConfig: Doc<WorkdayIntegrationConfig> = {
          id: orgId,
          collection: COLLECTION,
          data: {
            orgId,
            active: false,
            createdAt: n,
            createdBy: userId,
            editedAt: n,
            editedBy: userId,

            importApiUrl: '',
            username: '',
            password: '',
            endpoints: [
              {
                id: shortid(),
                name: '',
                url: '',
                username: '',
                password: ''
              }
            ]
          }
        };
        return newConfig;
      }
      return d;
    }
  );
};

export const removeWorkdayConfig = (orgId: string) => {
  return collection().doc(orgId).delete();
};

/**
 * The org's curated Workday write-field catalog, for the subtask Data
 * Destinations picker. Reads only this org's `config.writeFields` — a new org
 * with nothing configured returns [] (empty picker), never another org's fields.
 * Shape matches the picker's `{ key, label }` consumption (WorkdayWriteField is
 * a superset with `type`).
 */
export const useWorkdayDataDestinationFields = (orgId: string) =>
  useMappedLoadingValue(
    useDocumentListener(workdayConfigStore(orgId)),
    (d): WorkdayWriteField[] => d?.data.writeFields ?? []
  );

/**
 * Persist the write-field catalog for an org. Direct merge-write (no credential
 * re-verification) — mirrors the import-mapping save pattern. The config doc
 * already exists whenever this editor is reachable (Workday is set up).
 */
export const saveWorkdayWriteFields = (
  orgId: string,
  writeFields: WorkdayWriteField[]
) => collection().doc(orgId).set({ writeFields }, { merge: true });

// ---------------------------------------------------------------------------
// Live custom-object discovery (customObjectDefinition API) — powers the
// two-level field-mapping picker (object -> field), ADP/Salesforce-style.
// Replaces the hand-maintained writeFields catalog for the subtask picker.
// ---------------------------------------------------------------------------

/** A REST-writable custom object (effective-dated/SOAP objects are excluded BE-side). */
export type WorkdayCustomObjectDef = {
  id: string;
  alias: string;
  name: string;
  multiInstance: boolean;
  businessObject: string;
  effectiveDated: boolean;
};

/**
 * How a field's value is shaped on write (from the definitions-API `validations`):
 * `scalar` writes the raw value, `reference` → `{ id }`, `referenceList` → `[{ id }]`.
 * Mirrors the backend `WorkdayFieldKind`; a reference value must be a Workday instance id.
 */
export type WorkdayFieldKind = 'scalar' | 'reference' | 'referenceList';

/** A field of a custom object. `kind` drives reference value shaping + picker guidance. */
export type WorkdayObjectField = {
  id: string;
  alias: string;
  name: string;
  fieldType: string;
  kind: WorkdayFieldKind;
};

/** Lists the org's REST-writable custom objects (for the object dropdown). */
export const useWorkdayCustomObjects = (orgId: string) =>
  useFirebaseFunction<{ objects: WorkdayCustomObjectDef[] }>(
    'workday_api_describeCustomObjects',
    { orgId },
    [orgId]
  );

/** Lists one object's fields (for the field dropdown). `definitionId` = object id. */
export const useWorkdayObjectFields = (orgId: string, definitionId: string) =>
  useFirebaseFunction<{ fields: WorkdayObjectField[] }>(
    'workday_api_getDataDestinationFields',
    definitionId ? { orgId, definitionId } : undefined,
    [orgId, definitionId]
  );

/** One Workday reference (ID + display name) for the business-process settings dropdowns. */
export type WorkdayReferenceOption = { id: string; descriptor: string };

/** Reference options of one type (reasons, pay plans, currencies, …) via SOAP Get_References. */
export const useWorkdayReferenceOptions = (
  orgId: string,
  referenceType: string,
  descriptorPrefix?: string
) =>
  useFirebaseFunction<{ options: WorkdayReferenceOption[] }>(
    'workday_soap_getReferenceOptions',
    orgId && referenceType
      ? { orgId, referenceType, ...(descriptorPrefix ? { descriptorPrefix } : {}) }
      : undefined,
    [orgId, referenceType, descriptorPrefix],
    5 * 60 * 1000
  );

/** Issues-bar Retry on a WORKDAY_WRITE_FAILED row: re-queues the experience's unfinished Workday writes. */
export const retryWorkdaySubtaskWrite = (input: { orgId: string; experienceId: string }) =>
  callFirebaseFunction<{ success: true; requeuedOpCount: number }>(
    'workday_api_retrySubtaskWrite',
    input
  );
